import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { IMAGES } from "../../lib/site";

// ============================================================
//  BANNER BACKGROUND VIDEO
//  To use your Vimeo video, paste its numeric ID below.
//  e.g. for https://vimeo.com/824804225  ->  VIMEO_ID = "824804225"
//  Leave it as "" to keep showing the placeholder image.
//  (For a private/unlisted video, append the hash: "824804225?h=abc123")
// ============================================================
const VIMEO_ID = "1210899116";

const vimeoSrc = (id) => {
  const [videoId, hash] = id.split("?h=");
  const params = new URLSearchParams({
    background: "1", // hides all controls, autoplays, loops, mutes
    autoplay: "1",
    loop: "1",
    muted: "1",
    autopause: "0",
  });
  if (hash) params.set("h", hash);
  return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
};

export const Banner = () => {
  const secRef = useRef(null);
  const iframeRef = useRef(null);

  // Pause the showreel as soon as the banner scrolls off-screen (stops it
  // decoding/downloading in the background); resume when it comes back.
  useEffect(() => {
    if (!VIMEO_ID || !secRef.current) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        try {
          iframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({ method: e.isIntersecting ? "play" : "pause" }),
            "https://player.vimeo.com"
          );
        } catch {}
      },
      { threshold: 0.02 }
    );
    obs.observe(secRef.current);
    return () => obs.disconnect();
  }, []);

  return (
  <section
    ref={secRef}
    id="banner"
    data-testid="top-banner"
    className="relative h-[75vh] min-h-[520px] w-full overflow-hidden bg-slate-900"
  >
    {/* ---- BACKGROUND MEDIA ---- */}
    {VIMEO_ID ? (
      // Vimeo background video, scaled to fully cover the banner (no bars).
      <div className="pointer-events-none absolute inset-0 overflow-hidden" data-testid="banner-media">
        <iframe
          ref={iframeRef}
          title="Birds Eye Views showreel"
          src={vimeoSrc(VIMEO_ID)}
          allow="autoplay; fullscreen"
          frameBorder="0"
          className="absolute left-1/2 top-1/2 h-[56.25vw] min-h-full w-[177.78vh] min-w-full -translate-x-1/2 -translate-y-1/2"
        />
      </div>
    ) : (
      <img
        src={IMAGES.bannerPlaceholder}
        alt="Aerial drone footage over farmland"
        className="absolute inset-0 h-full w-full object-cover"
        data-testid="banner-media"
      />
    )}

    {/* ---- Subtle overlay to keep the heading readable (light, no heavy shadow) ---- */}
    <div className="absolute inset-0 bg-black/20" />

    {/* ---- Dashed frame marking this as a background-video slot (shown only while empty) ---- */}
    {!VIMEO_ID && (
      <>
        <div className="pointer-events-none absolute inset-4 rounded-lg border border-dashed border-white/25 sm:inset-6" />
        <div className="pointer-events-none absolute right-6 top-6 rounded-full bg-black/40 px-3 py-1 font-sans text-[10px] font-medium uppercase tracking-[0.2em] text-white/70 backdrop-blur-sm sm:right-8 sm:top-8">
          Background video · to be added
        </div>
      </>
    )}

    {/* ---- Centered heading ---- */}
    <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center">
      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="font-serif text-5xl font-medium tracking-tight text-white drop-shadow-lg sm:text-6xl lg:text-7xl"
      >
        Birds Eye Views
      </motion.h1>
      <motion.span
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="mt-6 block h-px w-40 origin-center bg-white/80 sm:w-56"
      />
    </div>
  </section>
  );
};
